// Growth section for Analytics — sign-ups, downloads, segments
import { useMemo, useState } from "react";
import { computeGrowthAnalytics } from "../lib/growthAnalyticsMetrics";
import { contactCreatedAtMs } from "../lib/db";
import { segmentProfession } from "../lib/growthAnalyticsMetrics";
import {
  formatPreferredFormat,
  formatProjectTypes,
} from "../lib/contactProfileFields";
import MetricGrid from "./MetricGrid";
import AreaChart from "./charts/AreaChart";
import BarChart from "./charts/BarChart";
import SignupComparisonChart from "./charts/SignupComparisonChart";
import SankeyChart from "./charts/SankeyChart";
import ChartExpandModal, { ChartExpandButton } from "./charts/ChartExpandModal";
import DateRangeFilter from "./charts/DateRangeFilter";
import SegmentCard from "./SegmentCard";
import { computeActivationSankey } from "../lib/activationSankeyMetrics";
import { resolveDateRange } from "../lib/dateRangeFilter";
import { UserPlus, TrendingUp, Calendar, Download, Award, Briefcase } from "lucide-react";

function fmtPct(v) {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
}

function fmtAvg(v) {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function GrowthAnalyticsSection({
  contacts,
  files,
  payments = [],
  orders = [],
  scans = [],
  workspaces = [],
  workspaceMembers = [],
  productAnalyticsEvents = [],
  onDrill,
}) {
  const [expandedChart, setExpandedChart] = useState(null);
  const [activationDateRange, setActivationDateRange] = useState({ preset: "all" });

  const activationRangeResolved = useMemo(
    () => resolveDateRange(activationDateRange),
    [activationDateRange]
  );

  const growth = useMemo(
    () => computeGrowthAnalytics(contacts, files, { payments, orders }),
    [contacts, files, payments, orders]
  );

  const activationSankey = useMemo(
    () =>
      computeActivationSankey(
        contacts,
        workspaces,
        workspaceMembers,
        files,
        orders,
        scans,
        productAnalyticsEvents,
        activationDateRange
      ),
    [contacts, workspaces, workspaceMembers, files, orders, scans, productAnalyticsEvents, activationDateRange]
  );

  const eq = growth.signupMtdEquivalent;
  const dailyPoints = growth.dailySignupsLast90.map((r) => ({ label: r.date, value: r.count }));
  const dailyLabels = dailyPoints.map((r) => r.label.slice(5));
  const dailyValues = dailyPoints.map((r) => r.value);
  const monthLabels = growth.monthlySignupsSeries.map((m) => m.label);
  const monthValues = growth.monthlySignupsSeries.map((m) => m.count);

  const downloadNote = growth.downloadsAreExplicit
    ? "Explicit download events from `files`."
    : "File activity by upload/created date.";

  const growthKpis = [
    {
      key: "today",
      icon: UserPlus,
      label: "Sign-ups today",
      value: String(growth.signupsToday),
      sub: `${growth.signupsLast7} in last 7d · WoW ${fmtPct(growth.wowSignupGrowthPct)}`,
      drill: { type: "growth_drill", growthKey: "signups_today", title: `Sign-ups today` },
    },
    {
      key: "mtd",
      icon: Calendar,
      label: "Sign-ups (same period)",
      value: String(eq?.current ?? growth.signupsThisMonth),
      sub: `${eq?.growthLabel || ""} · ${eq?.comparisonText || ""}`,
      drill: { type: "growth_drill", growthKey: "signups_this_month", title: "Sign-ups this period" },
    },
    {
      key: "avg30",
      icon: TrendingUp,
      label: "Avg daily sign-ups (30d)",
      value: fmtAvg(growth.avgDailySignups30d),
      sub: `${growth.signupsLast30} in last 30 days`,
      drill: { type: "growth_drill", growthKey: "daily_signups_history", title: "Daily sign-up history" },
    },
    {
      key: "dl_overall",
      icon: Download,
      label: "Avg daily file events (overall)",
      value: fmtAvg(growth.avgDailyDownloadsOverall),
      sub: `${growth.totalDownloads} events · ${growth.overallDays} day span`,
      drill: null,
    },
    {
      key: "dl_month",
      icon: Download,
      label: "File events this month",
      value: String(growth.downloadsThisMonth),
      sub:
        growth.downloadsThisMonth === 0
          ? `0 in ${new Date().toLocaleString(undefined, { month: "long" })} · ${growth.signupsThisMonth} sign-ups (different metric)`
          : `Avg ${fmtAvg(growth.avgDailyDownloadsThisMonth)}/day this month`,
      drill: null,
    },
    {
      key: "peak",
      icon: Award,
      label: "Peak sign-up day",
      value: growth.peakDay ? String(growth.peakDay.count) : "—",
      sub: growth.peakDay?.date || "—",
      drill: { type: "growth_drill", growthKey: "daily_signups_history", title: "Daily history" },
    },
    {
      key: "profession",
      icon: Briefcase,
      label: "Onboarding · profession",
      value: growth.onboarding.professionPct == null ? "—" : `${growth.onboarding.professionPct.toFixed(0)}%`,
      sub: `${growth.onboarding.withProfession}/${growth.signupsTotal}`,
      drill: { type: "growth_drill", growthKey: "segment_profession", title: "By profession" },
    },
  ];

  const segmentBlocks = [
    { title: "Profession", labelFn: segmentProfession, drillKey: "segment_profession" },
    {
      title: "Preferred format",
      labelFn: (c) => formatPreferredFormat(c.preferredFormat, c.preferredFormatOther) || "Not set",
      drillKey: "segment_format",
    },
    {
      title: "Project types",
      labelFn: (c) => {
        const t = formatProjectTypes(c.projectTypes, c.projectTypeOther);
        return t ? t.split(",").map((s) => s.trim()) : ["Not set"];
      },
      drillKey: "segment_project",
    },
  ];

  const signupComparison = (
    <SignupComparisonChart series={growth.monthSignupComparison} equivalent={eq} height={200} compact />
  );

  const handleSankeyNodeClick = onDrill
    ? (node) => {
        const stageKey = node.key || node.id;
        onDrill({
          type: "activation_drill",
          stageKey,
          title: `Activation · ${node.label || node.shortLabel || stageKey}`,
          dateRange: activationDateRange,
        });
      }
    : null;

  const activationRangeNote = activationRangeResolved.isAllTime
    ? `${activationSankey.signupsTotal} sign-ups (all time)`
    : activationRangeResolved.invalid
      ? "Select a valid custom date range"
      : `${activationSankey.signupsTotal} sign-ups · ${activationRangeResolved.label}`;

  const sankeyChart = (
    <SankeyChart
      nodes={activationSankey.nodes}
      links={activationSankey.links}
      height={260}
      onNodeClick={handleSankeyNodeClick}
    />
  );

  const dailyChart = (
    <AreaChart
      values={dailyValues}
      points={dailyPoints}
      labels={dailyLabels.filter((_, i) => i % 15 === 0 || i === dailyLabels.length - 1)}
      height={280}
    />
  );

  const monthlyChart = (
    <BarChart values={monthValues} labels={monthLabels} height={220} accent="rgba(52, 211, 153, 0.65)" />
  );

  return (
    <section className="analytics-section">
      <header className="page-header" style={{ marginBottom: 16 }}>
        <h2 className="text-h3">Growth</h2>
        <p className="page-header-desc">
          Sign-ups use same-period comparison ({growth.timezoneLabel}). {downloadNote} Hover charts for values; use expand for a larger view.
        </p>
      </header>

      <div className="growth-top-grid" style={{ marginBottom: 20 }}>
        <div className="card chart-hero-card chart-hero-card--compact">
          <div className="section-header" style={{ marginBottom: 4 }}>
            <div className="section-title" style={{ fontSize: 13 }}>Sign-ups · same period vs last month</div>
            <ChartExpandButton onClick={() => setExpandedChart("signup-comparison")} />
          </div>
          {signupComparison}
        </div>
        <div className="card chart-hero-card">
          <div className="section-header" style={{ marginBottom: 8 }}>
            <div>
              <div className="section-title">Activation drop-off</div>
              <div className="section-sub">
                Cohort by sign-up date · {activationRangeNote} · main path: onboarding → workspace → started scan
              </div>
            </div>
            <ChartExpandButton onClick={() => setExpandedChart("sankey")} />
          </div>
          <DateRangeFilter
            value={activationDateRange}
            onChange={setActivationDateRange}
            compact
            className="activation-date-filter"
          />
          {activationRangeResolved.invalid ? (
            <p className="text-caption" style={{ padding: "12px 0" }}>Pick both start and end dates for a custom range.</p>
          ) : (
            sankeyChart
          )}
        </div>
      </div>

      <MetricGrid items={growthKpis} onDrill={onDrill} />

      <div className="card chart-secondary-card" style={{ marginBottom: 16 }}>
        <div className="section-header">
          <div>
            <div className="section-title">Daily sign-ups · last 90 days</div>
            <div className="section-sub">{growth.timezoneLabel}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <ChartExpandButton onClick={() => setExpandedChart("daily")} />
            {onDrill && (
              <button type="button" className="btn btn-sm" onClick={() => onDrill({ type: "growth_drill", growthKey: "daily_signups_history", title: "Daily sign-up history" })}>
                View table
              </button>
            )}
          </div>
        </div>
        {dailyValues.every((v) => v === 0) ? (
          <p className="text-caption" style={{ padding: 24 }}>No sign-ups in range.</p>
        ) : (
          dailyChart
        )}
      </div>

      <div className="card chart-secondary-card" style={{ marginBottom: 20 }}>
        <div className="section-header">
          <div>
            <div className="section-title">Monthly sign-ups</div>
            <div className="section-sub">Last {growth.monthlySignupsSeries.length} months</div>
          </div>
          <ChartExpandButton onClick={() => setExpandedChart("monthly")} />
        </div>
        {monthValues.every((v) => v === 0) ? (
          <p className="text-caption" style={{ padding: 24 }}>No monthly data.</p>
        ) : (
          monthlyChart
        )}
      </div>

      <h3 className="text-h3" style={{ marginBottom: 12 }}>User segments</h3>
      <div className="segment-grid">
        {segmentBlocks.map((block) => (
          <SegmentCard
            key={block.title}
            title={block.title}
            contacts={contacts}
            labelFn={block.labelFn}
            getTimestampMs={contactCreatedAtMs}
            drillKey={block.drillKey}
            onDrill={
              onDrill
                ? (key, title) => onDrill({ type: "growth_drill", growthKey: key, title: `Segment · ${title}` })
                : null
            }
          />
        ))}
      </div>

      <h3 className="text-h3" style={{ margin: "20px 0 12px" }}>Additional breakdowns</h3>
      <div className="segment-grid">
        <div className="card">
          <div className="section-title" style={{ fontSize: 13, marginBottom: 10 }}>Order status</div>
          <p className="text-caption" style={{ marginBottom: 10 }}>Paid order or payment vs not yet — not subscription plan.</p>
          {(growth.orderStatusSegments || []).map((row) => (
            <div key={row.label} style={{ marginBottom: 8, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span>{row.label}</span>
                <span>{row.count} ({row.pct.toFixed(0)}%)</span>
              </div>
              <div className="funnel-stage-track">
                <div className="funnel-stage-fill" style={{ width: `${row.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
        <SegmentCard
          title="Industry"
          contacts={contacts}
          labelFn={(c) => c.industry?.trim() || "Not set"}
          getTimestampMs={contactCreatedAtMs}
        />
      </div>

      <ChartExpandModal
        open={expandedChart === "signup-comparison"}
        title="Sign-ups · same period vs last month"
        subtitle={`${growth.timezoneLabel} · hover points for daily and cumulative counts`}
        onClose={() => setExpandedChart(null)}
      >
        <SignupComparisonChart series={growth.monthSignupComparison} equivalent={eq} height={420} />
      </ChartExpandModal>

      <ChartExpandModal
        open={expandedChart === "sankey"}
        title="Activation drop-off"
        subtitle={`Cohort by sign-up date · ${activationRangeNote}. Main path: onboarding → workspace → started scan → upload → orders. Optional branch: profile.`}
        onClose={() => setExpandedChart(null)}
      >
        <DateRangeFilter
          value={activationDateRange}
          onChange={setActivationDateRange}
          className="activation-date-filter"
        />
        {activationRangeResolved.invalid ? (
          <p className="text-caption" style={{ padding: 24 }}>Pick both start and end dates for a custom range.</p>
        ) : (
          <SankeyChart
            nodes={activationSankey.nodes}
            links={activationSankey.links}
            height={440}
            onNodeClick={handleSankeyNodeClick}
          />
        )}
      </ChartExpandModal>

      <ChartExpandModal
        open={expandedChart === "daily"}
        title="Daily sign-ups · last 90 days"
        subtitle={growth.timezoneLabel}
        onClose={() => setExpandedChart(null)}
      >
        <AreaChart values={dailyValues} points={dailyPoints} labels={dailyLabels.filter((_, i) => i % 7 === 0 || i === dailyLabels.length - 1)} height={420} />
      </ChartExpandModal>

      <ChartExpandModal
        open={expandedChart === "monthly"}
        title="Monthly sign-ups"
        subtitle={`Last ${growth.monthlySignupsSeries.length} months`}
        onClose={() => setExpandedChart(null)}
      >
        <BarChart values={monthValues} labels={monthLabels} height={380} accent="rgba(52, 211, 153, 0.65)" />
      </ChartExpandModal>
    </section>
  );
}
